import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, ArrowLeft, Building2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

const AtelierLogin = ({ onLogin }) => {
  const [step, setStep] = useState(1); // 1 = garage code, 2 = PIN
  const [garageCode, setGarageCode] = useState('');
  const [garageName, setGarageName] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { setTimeout(() => setMounted(true), 100); }, []);
  useEffect(() => { if (step === 1 && inputRef.current) inputRef.current.focus(); }, [step]);

  const verifyGarage = async () => {
    if (!garageCode.trim()) { toast.error("Entrez le code du garage"); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/atelier-verify/${garageCode.trim()}`);
      setGarageName(res.data.garage_name);
      setStep(2);
      setPin('');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code garage inconnu");
      setGarageCode('');
    } finally { setLoading(false); }
  };

  const loginWithPin = async (fullPin) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/atelier-access`, {
        garage_code: garageCode.trim(),
        code: fullPin,
      });
      const emp = res.data.employee;
      localStorage.setItem('atelier_employee', JSON.stringify({
        id: emp.id, name: `${emp.first_name} ${emp.last_name}`.trim(),
        email: emp.email, role: emp.role, tenant_id: emp.tenant_id,
        token: res.data.access_token,
      }));
      onLogin(res.data.access_token);
      toast.success("Bienvenue a l'atelier !");
      navigate('/atelier/planning');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code PIN incorrect");
      setPin('');
    } finally { setLoading(false); }
  };

  const handleDigit = (digit) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      if (newPin.length === 4) {
        setTimeout(() => loginWithPin(newPin), 200);
      }
    }
  };

  const isMobile = window.innerWidth <= 640;
  const bgImage = isMobile ? '/vortekio-bg-mobile.png' : '/vortekio-bg.png';

  return (
    <>
      <style>{`
        @keyframes float-at { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes pulse-orange { 0%, 100% { box-shadow: 0 0 20px rgba(234,88,12,0); } 50% { box-shadow: 0 0 35px rgba(234,88,12,0.25); } }
        @keyframes slide-up-at { from { opacity: 0; transform: translateY(25px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gradient-at { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .at-overlay { background: linear-gradient(135deg, rgba(15,23,42,0.4), rgba(0,0,0,0.15), rgba(15,23,42,0.5)); background-size: 200% 200%; animation: gradient-at 8s ease infinite; }
        .at-float { animation: float-at 5s ease-in-out infinite; }
        .at-glow { animation: pulse-orange 4s ease-in-out infinite; }
        .at-slide-1 { animation: slide-up-at 0.6s ease-out 0.1s both; }
        .at-slide-2 { animation: slide-up-at 0.6s ease-out 0.3s both; }
        .at-fade { animation: fade-in 0.4s ease-out both; }
        .num-btn { transition: all 0.15s ease; }
        .num-btn:active { transform: scale(0.92); }
        .garage-input:focus { border-color: rgba(234,88,12,0.6) !important; box-shadow: 0 0 0 2px rgba(234,88,12,0.2); }
      `}</style>

      <div
        className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
        style={{ backgroundImage: `url('${bgImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 at-overlay" />

        <div className={`w-full flex flex-col items-center relative z-10 transition-all duration-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          <div className="at-float mb-3 at-slide-1">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Wrench className="w-8 h-8 text-white" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-white mb-1 at-slide-1">Poste Atelier</h1>

          <div className="w-full max-w-xs">
            {/* STEP 1: Garage Code */}
            {step === 1 && (
              <div className="at-slide-2">
                <p className="text-sm text-slate-400 mb-5 text-center">Entrez le code du garage</p>
                <div
                  className="w-full p-5 rounded-2xl at-glow"
                  style={{ background: 'rgba(15, 20, 30, 0.8)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="step-garage-code"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-4 h-4 text-orange-400" />
                    <span className="text-xs text-slate-400 font-medium">Code garage</span>
                  </div>
                  <Input
                    ref={inputRef}
                    type="text"
                    value={garageCode}
                    onChange={(e) => setGarageCode(e.target.value.toUpperCase())}
                    placeholder="Ex: GDV"
                    maxLength={10}
                    className="h-14 text-center text-3xl tracking-[0.3em] rounded-xl font-bold bg-slate-800/60 border-white/10 text-white placeholder:text-slate-600 garage-input transition-all duration-300 uppercase"
                    data-testid="garage-code-input"
                    onKeyDown={(e) => e.key === 'Enter' && verifyGarage()}
                  />
                  <Button
                    onClick={verifyGarage}
                    disabled={!garageCode.trim() || loading}
                    className="w-full h-11 mt-4 rounded-xl font-semibold text-sm text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-30 disabled:bg-slate-800 transition-all"
                    data-testid="verify-garage-btn"
                  >
                    {loading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                    ) : "Continuer"}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: PIN */}
            {step === 2 && (
              <div className="at-fade">
                <div className="flex items-center justify-center gap-2 mb-5">
                  <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-600/20 border border-orange-500/30">
                    <Check className="w-3 h-3 text-orange-400" />
                    <span className="text-xs font-medium text-orange-300">{garageCode}</span>
                  </div>
                  <span className="text-xs text-slate-500">{garageName}</span>
                </div>

                <div
                  className="w-full p-5 rounded-2xl at-glow"
                  style={{ background: 'rgba(15, 20, 30, 0.8)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="step-pin"
                >
                  <p className="text-xs text-slate-400 text-center mb-4">Code d'acces atelier</p>

                  {/* PIN dots */}
                  <div className="flex justify-center gap-4 mb-5">
                    {[0, 1, 2, 3].map(i => (
                      <div
                        key={i}
                        className={`w-4 h-4 rounded-full transition-all duration-200 ${
                          i < pin.length
                            ? 'bg-orange-500 scale-110 shadow-lg shadow-orange-500/40'
                            : 'bg-slate-700 border border-slate-600'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Numpad */}
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
                      <button
                        key={digit}
                        onClick={() => handleDigit(String(digit))}
                        disabled={loading || pin.length >= 4}
                        className="num-btn h-14 rounded-xl text-xl font-bold text-white bg-slate-800/70 hover:bg-orange-600/30 border border-slate-700 hover:border-orange-500/50 disabled:opacity-30"
                        data-testid={`numpad-${digit}`}
                      >
                        {digit}
                      </button>
                    ))}
                    <button
                      onClick={() => setPin(pin.slice(0, -1))}
                      disabled={loading || pin.length === 0}
                      className="num-btn h-14 rounded-xl text-xs font-medium text-slate-400 bg-slate-800/40 hover:bg-red-600/20 border border-slate-700 hover:border-red-500/50 disabled:opacity-30"
                      data-testid="numpad-delete"
                    >
                      Effacer
                    </button>
                    <button
                      onClick={() => handleDigit('0')}
                      disabled={loading || pin.length >= 4}
                      className="num-btn h-14 rounded-xl text-xl font-bold text-white bg-slate-800/70 hover:bg-orange-600/30 border border-slate-700 hover:border-orange-500/50 disabled:opacity-30"
                      data-testid="numpad-0"
                    >
                      0
                    </button>
                    <button
                      onClick={() => { setStep(1); setGarageCode(''); setGarageName(''); setPin(''); }}
                      className="num-btn h-14 rounded-xl text-xs font-medium text-slate-400 bg-slate-800/40 hover:bg-slate-700 border border-slate-700 disabled:opacity-30 flex items-center justify-center gap-1"
                      data-testid="back-btn"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Retour
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center mt-3 space-x-2">
              <Button variant="link" onClick={() => navigate('/scan')} className="text-xs text-slate-400 hover:text-white transition-colors">
                Espace employe
              </Button>
              <span className="text-slate-600">|</span>
              <Button variant="link" onClick={() => navigate('/admin/login')} className="text-xs text-slate-400 hover:text-white transition-colors">
                Administration
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AtelierLogin;
